import React, { useState } from 'react';
import styled from 'styled-components';

import { CardSkeleton } from '../../Skeleton';
import Typography from 'kingpin/atoms/Typography';
import moment from 'moment';
import Row from '../../Common/Row';
import FlexCentered from '../../Common/FlexCentered';
import { Img } from '../../../screens/LegacyHome/RecentsCard/RecentsCard';
import NoNotificationsSrc from '../../NotificationsPanel/notifications.svg';
import { HEADER_SPAN_CSS, SPAN_CSS } from 'components/Grid/StyleOverrides';

const List = styled.div`
  height: 100%;
  overflow-y: auto;
  overflow-x: auto;
`;

const DateRow = styled.div<{ isToday?: boolean; isOverdue?: boolean }>`
  ${(props) => props.isToday && `background-color: var(--color-avatar-1-bg);`}
  ${(props) => !props.isToday && `background-color: var(--color-bg-subtle);`}
      ${(props) =>
    props.isOverdue && `background-color: var(--color-bg-subtle);`}

  padding: 4px 16px;
  margin-bottom: 8px;
  display: flex;
  justifycontent: space-between;
  align-items: center;

  span {
    ${HEADER_SPAN_CSS}
  }
`;

const ItemRow = styled.div`
  padding: 0px 16px;

  &:not(:last-of-type) {
    margin-bottom: 8px;
    border-bottom: 1px solid var(--color-border-secondary);
    padding-bottom: 8px;
  }

  span {
    ${SPAN_CSS}
  }
`;

const Col = styled.div<{ flex: number }>`
  display: flex;
  flex: ${(props) => props.flex};
  flex-grow: 1;
  overflow: hidden;
`;

const BadgeDiv = styled.div<{
  borderColor: string;
  backgroundColor: string;
}>`
  display: inline-flex;
  max-width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;

  background: ${(props) => props.backgroundColor};
  border-radius: 16px;
  border: 1px solid ${(props) => props.borderColor};
`;

const NoEvents = () => (
  <FlexCentered style={{ height: '100%' }}>
    <Img src={NoNotificationsSrc} />
    <Typography.Body type="Body 12">No events found</Typography.Body>
  </FlexCentered>
);

const getBackgroundColor = (eventName: string) => {
  if (eventName === 'Driver License Expiry') {
    return 'var(--color-avatar-1-bg)';
  } else if (eventName === 'Next Physical') {
    return 'var(--color-avatar-2-bg)';
  } else if (eventName === 'Driver Birthday') {
    return 'var(--color-avatar-3-bg)';
  } else if (eventName === 'Work Anniversary') {
    return 'var(--color-avatar-4-bg)';
  } else if (eventName === 'Review Date') {
    return 'var(--color-avatar-5-bg)';
  } else if (eventName === 'Projected Home Date') {
    return 'var(--color-avatar-6-bg)';
  } else if (eventName === 'Projected Return Date') {
    return 'var(--color-avatar-7-bg)';
  } else {
    return 'var(--color-avatar-8-bg)';
  }
};

const getBorderColor = (eventName: string) => {
  if (eventName === 'Driver License Expiry') {
    return 'var(--color-avatar-1-text)';
  } else if (eventName === 'Next Physical') {
    return 'var(--color-avatar-2-text)';
  } else if (eventName === 'Driver Birthday') {
    return 'var(--color-avatar-3-text)';
  } else if (eventName === 'Work Anniversary') {
    return 'var(--color-avatar-4-text)';
  } else if (eventName === 'Review Date') {
    return 'var(--color-avatar-5-text)';
  } else if (eventName === 'Projected Home Date') {
    return 'var(--color-avatar-6-text)';
  } else if (eventName === 'Projected Return Date') {
    return 'var(--color-avatar-7-text)';
  } else {
    return 'var(--color-avatar-8-text)';
  }
};

const getMiddleColumn = ({
  reminder,
  useDriverCode,
}: {
  reminder: Reminder;
  useDriverCode: boolean;
}) => {
  if (useDriverCode) {
    if (reminder.driver) {
      return reminder.driver;
    } else if (reminder.driverName) {
      return reminder.driverName;
    } else if (reminder.employeeName) {
      return reminder.employeeName;
    } else if (reminder.truck) {
      return reminder.truck;
    } else {
      return '-';
    }
  }

  if (reminder.driverName) {
    return reminder.driverName;
  } else if (reminder.driver) {
    return reminder.driver;
  } else if (reminder.employeeName) {
    return reminder.employeeName;
  } else if (reminder.truck) {
    return reminder.truck;
  } else {
    return '-';
  }
};

const ReminderBucket = ({
  reminder,
  useDriverCode,
}: {
  reminder: SortedReminders;
  useDriverCode: boolean;
}) => {
  const [isToday] = useState(
    () => moment().format('YYYY-MM-DD') === reminder.date,
  );
  const [isTomorrow] = useState(
    () => moment().add({ day: 1 }).format('YYYY-MM-DD') === reminder.date,
  );
  const [formattedDate] = useState(moment(reminder.date).format('ddd, MM/DD'));
  const left = formattedDate;
  const right = isToday ? ` | Today` : isTomorrow ? ` | Tomorrow` : '';

  return (
    <div style={{ marginBottom: 12 }}>
      <DateRow isToday={isToday}>
        <span>{`${left}${right}`}</span>
      </DateRow>
      <div>
        {reminder.reminders.map((r) => (
          <ItemRow key={r.key}>
            <Row centerAlign spaceBetween style={{ gap: 8 }}>
              <Col flex={1}>
                <BadgeDiv
                  borderColor={getBorderColor(r.name)}
                  backgroundColor={getBackgroundColor(r.name)}
                  style={{
                    overflow: 'hidden',
                    textWrap: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <span>{r.name}</span>
                </BadgeDiv>
              </Col>
              <Col flex={1}>
                <span style={{ color: 'var(--color-grey-2)' }}>
                  {getMiddleColumn({ reminder: r, useDriverCode })}
                </span>
              </Col>
              <Col flex={1}>
                <span>{r.description}</span>
              </Col>
            </Row>
          </ItemRow>
        ))}
      </div>
    </div>
  );
};

const OverdueBucket = ({
  reminders,
  useDriverCode,
}: {
  reminders: Reminder[];
  useDriverCode: boolean;
}) => {
  if (reminders.length === 0) {
    return null;
  }

  return (
    <div style={{ marginBottom: 12 }}>
      <DateRow isOverdue>
        <span>{`Overdue`}</span>
      </DateRow>
      <div>
        {reminders.map((r) => (
          <ItemRow key={r.key}>
            <Row centerAlign spaceBetween style={{ gap: 8 }}>
              <Col flex={1}>
                <BadgeDiv
                  borderColor={getBorderColor(r.name)}
                  backgroundColor={getBackgroundColor(r.name)}
                  style={{
                    overflow: 'hidden',
                    textWrap: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <span>{r.name}</span>
                </BadgeDiv>
              </Col>
              <Col
                flex={1}
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <span style={{ color: 'var(--color-grey-2)' }}>
                  {getMiddleColumn({ reminder: r, useDriverCode })}
                </span>
              </Col>
              <Col flex={1}>
                <span>
                  {r.description
                    ? r.description
                    : moment(r.date).format('ddd, MM/DD')}
                </span>
              </Col>
            </Row>
          </ItemRow>
        ))}
      </div>
    </div>
  );
};

const RemindersGadgetContent = ({
  pastReminders,
  futureReminders,
  isLoading,
  overdueReminders,
  hasEvents,
  useDriverCode,
}: {
  pastReminders: SortedReminders[];
  futureReminders: SortedReminders[];
  isLoading: boolean;
  overdueReminders: Reminder[];
  hasEvents: boolean;
  useDriverCode: boolean;
}) => {
  return (
    <List className={'hiding-scrollbar'}>
      {isLoading && <CardSkeleton />}
      {!isLoading && (
        <div style={{ width: '100%', height: '100%' }}>
          {hasEvents && (
            <>
              {pastReminders.map((r) => (
                <ReminderBucket
                  reminder={r}
                  key={r.date}
                  useDriverCode={useDriverCode}
                />
              ))}
              <OverdueBucket
                reminders={overdueReminders}
                useDriverCode={useDriverCode}
              />
              {futureReminders.map((r) => (
                <ReminderBucket
                  reminder={r}
                  key={r.date}
                  useDriverCode={useDriverCode}
                />
              ))}
            </>
          )}
          {!hasEvents && <NoEvents />}
        </div>
      )}
    </List>
  );
};

export default RemindersGadgetContent;
